import type { ITreeNode } from '../Itree-node';
import type { ITreeNodeEvent } from '../Itree-node-event';
import { TreeNodeEventType } from '../tree-node-event-type';
import { TreeNodeWrapper } from '../tree-node-wrapper';

// <summary>Returns ITreeNode wrapped around their source nodes</summary>
export function getWrappedTreeNodes(nodes: ITreeNode[]): ITreeNode[] {
	const wrappedNodes: TreeNodeWrapper[] = [];
	nodes.forEach(node => {
		wrappedNodes.push(new TreeNodeWrapper(node));
	});
	return wrappedNodes;
}

// <summary>create copies of TreeNodes</summary>
export function cloneNodes(treeNodes: ITreeNode[]): ITreeNode[] {
	return treeNodes.map((item: ITreeNode) => ({
		id: item.id,
		text: item.text,
		disableDragAndDrop: item.disableDragAndDrop,
		isSelected: item.isSelected,
		isEditable: item.isEditable,
		isOpened: item.isOpened,
		isVisible: item.isVisible,
		isHighlighted: false,
		children: cloneNodes(item.children),
		parentId: item.parentId
	}));
}

// <summary>Descend the structure of the passed in node applying the handler, the handler may recieve the result of handlers run on results below</summary>
export function walkTreeNodes(treeNodes: ITreeNode[], handler: (node: ITreeNode, subResult?: any, innerDepth?: number) => any, depth?: number): any[] {
	const myResults: any[] = [];
	treeNodes.forEach(n => {
		depth = depth || 1;
		myResults.push(handler(n, walkTreeNodes(n.children, handler, depth + 1), depth));
	});
	return myResults;
}

// <summary>turns hierarchical TreeNodes into a flat array</summary>
export function flattenNodes(hierarchicalNodes: ITreeNode[]): ITreeNode[] {
	const flattenedNodes: ITreeNode[] = [];
	walkTreeNodes(hierarchicalNodes, (node: ITreeNode) => {
		flattenedNodes.push(node);
	});
	return flattenedNodes;
}

export function unselectAllNodes(treeNodes: ITreeNode[]): void {
	walkTreeNodes(treeNodes, (n: ITreeNode) => {
		n.isSelected = false;
	});
}

export function openTreeNode(treeNode: ITreeNode | undefined): void {
	if (treeNode !== undefined) {
		treeNode.isOpened = true;
	}
}

export function childrenAreVisible(node: ITreeNode): boolean {
	return node.children.length > 0 && node.children.filter(n => !n.isVisible).length === 0 && node.isOpened;
}

export function showChildren(node: ITreeNode): void {
	if (node.children.length > 0) {
		if (!node.isOpened) {
			node.isOpened = true;
		}
		node.children
			.filter(t => !t.isVisible)
			.forEach(t => {
				t.isVisible = true;
			});
	}
}

export function openInitialNode(nodeId: number, treeNodes: ITreeNode[]): void {
	walkTreeNodes(treeNodes, (n: ITreeNode, subResults?: boolean[]) => {
		const childIsSelected: boolean = subResults !== undefined && subResults.filter(t => t).length > 0;
		const selected: boolean = n.id === nodeId;

		if (selected) {
			n.isSelected = true;
		}
		if (childIsSelected) {
			n.isOpened = true;
		}

		return childIsSelected || selected;
	});
}

export function encodeHtml(rawStr: string): string {
	return rawStr.replace(/[\u00A0-\u9999<>\&]/g, function(i) {
		return '&#' + i.charCodeAt(0) + ';';
	});
}

export function nodeSearchStringMatch(node: ITreeNode, expression: RegExp, encodedSearchExpression: RegExp, doHighlighting: boolean): boolean {
	const iMatch = expression.test(node.text);
	node.isHighlighted = iMatch && doHighlighting;
	if (node.isHighlighted) {
		const encodedText = encodeHtml(node.text);
		node.highlightedText = encodedText.toString().replace(encodedSearchExpression, function(matchedText) {
			return '<mark>' + matchedText + '</mark>';
		});
	}
	node.isOpened = node.children.length > 0;
	return iMatch;
}

export function doSearch(value: string, treeNodes: ITreeNode[]): void {
	const replacedValue = value.replace(' ', '\\s');
	const exp = new RegExp(replacedValue, 'i');
	const encodedSearchedText = encodeHtml(replacedValue);
	const encodedSearchExp = new RegExp(encodedSearchedText, 'i');

	walkTreeNodes(treeNodes, (n: ITreeNode, subResults?: boolean[]) => {
		n.isHighlighted = n.isVisible = false;

		// if an ancestor matches the search filter
		const matchedInChildren: boolean = subResults !== undefined && subResults.filter(t => t).length > 0;

		const iMatch: boolean = nodeSearchStringMatch(n, exp, encodedSearchExp, value !== '');

		// show the node if it matches or it has a ancestor match
		if (iMatch || matchedInChildren) {
			showChildren(n);
			n.isVisible = true;
		}

		// close the node if its children aren't visible
		if (n.children.filter(c => c.isVisible).length === 0) {
			n.isOpened = false;
		}

		return iMatch || matchedInChildren;
	});
}

export function handleTreeNodeEvent(event: ITreeNodeEvent, treeNodes: ITreeNode[], preventNodeClickOverride: boolean): void {
	const { node } = event;

	if (event.eventType === TreeNodeEventType.NodeHandleClicked) {
		node.isOpened = !node.isOpened;
	}

	// node handle clicks shouldn't select
	if (!node.isSelected && event.eventType !== TreeNodeEventType.NodeHandleClicked) {
		if (!preventNodeClickOverride) {
			unselectAllNodes(treeNodes);
			node.isSelected = true;
		}
	}
	if (node.isOpened) {
		if (!childrenAreVisible(node)) {
			showChildren(event.node);
		}
	}
}

export function addTreeNodesRecursively(treeNodes: ITreeNode[]): ITreeNode[] {
	const returnNodes: ITreeNode[] = [];

	function addTreeNodes(nodesToAdd: ITreeNode[], depth = 0): void {
		nodesToAdd.forEach(node => {
			node.depth = depth;
			if (node.isVisible) {
				returnNodes.push(node);
				if (node.isOpened) {
					addTreeNodes(node.children, depth + 1);
				}
			}
		});
	}

	addTreeNodes(treeNodes);

	return returnNodes;
}

export function toggleExpanded(isExpanded: boolean, treeNodes: ITreeNode[]): void {
	walkTreeNodes(treeNodes, (n: ITreeNode) => {
		n.isOpened = isExpanded;
		return true;
	});
}

export function initializeTreeNodes(selectedNodeId: number, openFirstVisibleNode: boolean, treeNodes: ITreeNode[]): void {
	if (selectedNodeId) {
		openInitialNode(selectedNodeId, treeNodes);
	}
	// open the first visible node
	else if (openFirstVisibleNode && treeNodes.length > 0) {
		openTreeNode(treeNodes.find(n => n.isVisible));
	}
}
